import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
  useCallback,
} from "react";
import api from "../services/ApiService";
import { useAuth } from "./AuthContext";

export type UserRole = "client" | "performer" | "admin" | "super_admin" | "dispatcher" | "supervisor" | "employee" | null;

interface RoleContextType {
  currentRole: UserRole;
  availableRoles: UserRole[];
  switchRole: (role: UserRole) => Promise<boolean>;
  loadUserRoles: () => Promise<void>;
  isLoading: boolean;
}

const RoleContext = createContext<RoleContextType | undefined>(undefined);

export { RoleContext };

export interface RoleProviderProps {
  children: ReactNode;
}

export const RoleProvider: React.FC<RoleProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [currentRole, setCurrentRole] = useState<UserRole>(null);
  const [availableRoles, setAvailableRoles] = useState<UserRole[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const loadUserRoles = useCallback(async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const roles: UserRole[] = [];

      // Проверяем роль через default_role или role
      const userRole =
        (user as any).default_role || (user as any).role || "client";

      if (userRole === "super_admin" || userRole === "admin") {
        roles.push("admin", "super_admin", "client", "performer");
      } else if (userRole === "dispatcher") {
        roles.push("dispatcher", "client");
      } else {
        roles.push("client", "performer");
      }

      setAvailableRoles(roles);

      // Восстанавливаем сохранённую роль или ставим роль по умолчанию
      const savedRole = localStorage.getItem(
        `selected_role_${user.id}`,
      ) as UserRole;
      if (savedRole && roles.includes(savedRole)) {
        setCurrentRole(savedRole);
      } else {
        // Для админов ставим admin по умолчанию, для диспетчеров - dispatcher, для остальных - client
        if (userRole === "super_admin" || userRole === "admin") {
          setCurrentRole("admin");
        } else if (userRole === "dispatcher") {
          setCurrentRole("dispatcher");
        } else {
          setCurrentRole("client");
        }
      }
    } catch (error) {
      console.error("Error loading user roles:", error);
      setCurrentRole("client");
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  const switchRole = useCallback(
    async (role: UserRole): Promise<boolean> => {
      if (!role || !availableRoles.includes(role)) {
        console.error("Invalid role or role not available:", role);
        return false;
      }

      setIsLoading(true);
      try {
        setCurrentRole(role);

        if (user?.id) {
          localStorage.setItem(`selected_role_${user.id}`, role);
        }

        console.log("Role switched to:", role);
        return true;
      } catch (error) {
        console.error("Error switching role:", error);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [availableRoles, user?.id],
  );

  useEffect(() => {
    if (user) {
      loadUserRoles();
    } else {
      setCurrentRole(null);
      setAvailableRoles([]);
    }
  }, [user]);

  const value = useMemo(
    () => ({
      currentRole,
      availableRoles,
      switchRole,
      loadUserRoles,
      isLoading,
    }),
    [currentRole, availableRoles, switchRole, loadUserRoles, isLoading],
  );

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
};

export const useRole = () => {
  const context = useContext(RoleContext);
  if (context === undefined) {
    throw new Error("useRole must be used within a RoleProvider");
  }
  return context;
};
